import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ImagePlus, X, Loader2, Film } from 'lucide-react';
import { fileToDataUrl } from '@/lib/utils';
import { uploadImage } from '@/lib/mediaUpload';
import { uploadVideoFile } from '@/lib/videoUpload';

export function EditPostDialog({ post, onClose, onUpdated }) {
  const [text, setText] = useState(post?.text || '');
  const [image, setImage] = useState(post?.image_data_url || null);
  const [videoUrl, setVideoUrl] = useState(post?.video_url || null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [newVideoPreview, setNewVideoPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef();
  const videoRef = React.useRef();

  const removeMedia = () => {
    setImage(null);
    setVideoUrl(null);
    setNewVideoFile(null);
    if (newVideoPreview) URL.revokeObjectURL(newVideoPreview);
    setNewVideoPreview(null);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    removeMedia();
    if (f.type.startsWith('video/')) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error('Video too large — 100 MB max');
        return;
      }
      setNewVideoFile(f);
      setNewVideoPreview(URL.createObjectURL(f));
    } else {
      try {
        setImage(await fileToDataUrl(f));
      } catch {
        toast.error('Failed to read image');
      }
    }
    e.target.value = '';
  };

  const onVideoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    if (!f.type.startsWith('video/')) {
      toast.error("That file isn't a video — please select a video");
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error('Video too large — 100 MB max');
      return;
    }
    removeMedia();
    setNewVideoFile(f);
    setNewVideoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!text.trim() && !image && !videoUrl && !newVideoFile) {
      toast.error('A post needs a caption, photo or video');
      return;
    }
    setSaving(true);
    setProgress(0);
    try {
      let finalVideo = videoUrl;
      let finalPoster = videoUrl ? post?.poster_url || null : null;
      if (newVideoFile) {
        const up = await uploadVideoFile(newVideoFile, setProgress);
        finalVideo = up.video_url;
        finalPoster = up.poster_url;
      }
      const r = await api.patch(`/posts/${post.id}`, {
        text: text.trim(),
        image_data_url: await uploadImage(image),
        video_url: finalVideo,
        poster_url: finalPoster,
      });
      toast.success('Post updated');
      onUpdated(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update post');
    } finally {
      setSaving(false);
    }
  };

  const mediaPreview = newVideoPreview || videoUrl;
  return (
    <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#0B0C10] border border-white/10 text-white" data-testid="edit-post-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Edit post</DialogTitle>
        </DialogHeader>
        <Textarea
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder="What's the vibe tonight?"
          className="min-h-[80px] resize-none bg-white/5 border-white/10 text-white placeholder:text-white/40"
          data-testid="edit-post-textarea"
        />
        {image && (
          <div className="relative overflow-hidden rounded-xl border border-white/10">
            <img src={image} alt="Post" className="max-h-60 w-full object-cover" />
            <button
              onClick={removeMedia}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
              data-testid="edit-post-remove-media-button"
            ><X className="h-4 w-4" /></button>
          </div>
        )}
        {!image && mediaPreview && (
          <div className="relative overflow-hidden rounded-xl border border-white/10">
            <video src={mediaPreview} controls playsInline className="max-h-60 w-full bg-black" />
            <button
              onClick={removeMedia}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
              data-testid="edit-post-remove-media-button"
            ><X className="h-4 w-4" /></button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="edit-post-file-input" />
        <input ref={videoRef} type="file" hidden onChange={onVideoFile} data-testid="edit-post-video-input" />
        <DialogFooter className="flex-row justify-between gap-2 sm:flex-row sm:justify-between">
          <div className="flex gap-2">
            <Button
              type="button" variant="outline"
              onClick={() => fileRef.current?.click()}
              className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10"
              data-testid="edit-post-replace-media-button"
            >
              <ImagePlus className="mr-2 h-4 w-4" /> Photo
            </Button>
            <Button
              type="button" variant="outline"
              onClick={() => videoRef.current?.click()}
              className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10"
              data-testid="edit-post-replace-video-button"
            >
              <Film className="mr-2 h-4 w-4" /> Video
            </Button>
          </div>
          <Button
            onClick={save} disabled={saving}
            className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
            data-testid="edit-post-save-button"
          >
            {saving && newVideoFile ? `Uploading ${progress}%` : saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditPostDialog;
